import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, Table, LayoutList } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { FontSizeOption } from "@/components/ui/FontSizeSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface SettingsFormProps {
  form: UseFormReturn<{
    url: string;
    refreshInterval: number;
    showDescriptions: boolean;
    fontSize: FontSizeOption;
    displayOptions: {
      showTitle: boolean;
      showDescription: boolean;
      showItems: boolean;
      showColumns: boolean;
    };
    selectedSections: string[];
  }, any>;
  onSubmit: (values: any) => void;
  isCorsError: boolean;
}

const SettingsForm: React.FC<SettingsFormProps> = ({ form, onSubmit, isCorsError }) => {
  const selectedSections = form.watch("selectedSections") || [];

  const toggleSection = (section: string) => {
    const currentSections = form.getValues("selectedSections") || [];
    const newSections = currentSections.includes(section)
      ? currentSections.filter(s => s !== section)
      : [...currentSections, section];
    form.setValue("selectedSections", newSections);
    onSubmit(form.getValues());
  };

  const removeSection = (section: string) => {
    const currentSections = form.getValues("selectedSections") || [];
    const newSections = currentSections.filter(s => s !== section);
    form.setValue("selectedSections", newSections);
    onSubmit(form.getValues());
  };

  const sectionOptions = [
    { value: "chumash", label: "חומש יומי" },
    { value: "tehillim", label: "תהילים יומי" },
    { value: "tanya", label: "תניא יומי" },
    { value: "rambam-3", label: "רמב״ם - ג׳ פרקים" },
    { value: "rambam-1", label: "רמב״ם - פרק אחד" }
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="selectedSections"
          render={({ field }) => (
            <FormItem>
              <FormLabel>בחר שיעורים יומיים</FormLabel>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedSections.map((section) => {
                    const option = sectionOptions.find(opt => opt.value === section);
                    return (
                      <Badge key={section} variant="secondary" className="flex items-center gap-1">
                        {option?.label}
                        <button
                          type="button"
                          onClick={() => removeSection(section)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
                <Select 
                  onValueChange={toggleSection}
                  value=""
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="הוסף שיעור יומי" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sectionOptions.map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        disabled={selectedSections.includes(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>כתובת URL לנתונים (Supabase Edge Function)</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com/api/data" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="refreshInterval"
          render={({ field }) => (
            <FormItem>
              <FormLabel>מרווח רענון (בדקות)</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="0" 
                  max="60" 
                  placeholder="5" 
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Card>
          <CardHeader>
            <CardTitle>הגדרות תצוגה</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="displayOptions.showTitle"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>הצג כותרת</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayOptions.showDescription"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>הצג תיאור</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayOptions.showItems"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>הצג פריטים</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayOptions.showColumns"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>הצג עמודות</FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <FormField
          control={form.control}
          name="showDescriptions"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">הצג תיאורים</FormLabel>
                <p className="text-sm text-gray-500">
                  האם להציג תיאורים בכרטיסי זמנים (למשל בזמני היום)
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        {isCorsError && (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>אזהרת CORS</AlertTitle>
            <AlertDescription>
              <p className="mb-2">זוהתה בעיית CORS בגישה לנתונים. אפשר לשמור את ההגדרות, אך כדי שהנתונים יוצגו כהלכה, יש לעדכן את הגדרות פונקציית Edge.</p>
            </AlertDescription>
          </Alert>
        )}
        
        <Button type="submit" className="w-full">שמור הגדרות</Button>
      </form>
    </Form>
  );
};

export default SettingsForm;
